/**
 * Kiosk Utility Functions
 * 
 * Shared utilities for kiosk display screens.
 * These are separated to avoid code duplication and ensure consistency.
 * 
 * @module kiosk/utils
 */

import { createClient, SupabaseClient } from '@supabase/supabase-js';

// ============================================================================
// Environment Configuration
// ============================================================================

const metaEnv = (typeof import.meta !== 'undefined') ? (import.meta as any).env : undefined;
const supabaseUrl = (metaEnv && metaEnv.VITE_SUPABASE_URL) ||
    (typeof process !== 'undefined' ? process.env?.VITE_SUPABASE_URL : undefined);
const supabaseAnonKey = (metaEnv && metaEnv.VITE_SUPABASE_ANON_KEY) ||
    (typeof process !== 'undefined' ? process.env?.VITE_SUPABASE_ANON_KEY : undefined);

// ============================================================================
// JWT Utilities
// ============================================================================

/**
 * Decoded JWT payload structure for kiosk tokens
 */
export interface KioskJwtPayload {
    /** Unique kiosk/monitor identifier */
    kiosk_id?: string;
    /** Alternative monitor ID field */
    monitor_id?: string;
    /** Subject (fallback ID) */
    sub?: string;
    /** School ID this kiosk belongs to */
    school_id?: string;
    /** Role (should be 'kiosk') */
    role?: string;
    /** Display name of the kiosk */
    kiosk_name?: string;
    /** Views this kiosk is authorized to display */
    allowed_views?: string[];
    /** Token expiration (Unix timestamp in seconds) */
    exp?: number;
    /** Token issued at (Unix timestamp in seconds) */
    iat?: number;
}

/**
 * Decodes a JWT payload without verification (client-side only).
 * 
 * ⚠️ WARNING: This does NOT verify the token signature.
 * Server-side validation is still required for security.
 * 
 * @param token - The JWT string to decode
 * @returns The decoded payload or null if invalid
 */
export function decodeJwtPayload(token: string): KioskJwtPayload | null {
    try {
        const parts = token.split('.');
        if (parts.length !== 3) return null;

        // Convert base64url to base64
        const payloadBase64 = parts[1].replace(/-/g, '+').replace(/_/g, '/');
        const decoded = atob(payloadBase64);
        return JSON.parse(decoded);
    } catch {
        return null;
    }
}

/**
 * Gets the kiosk JWT from localStorage
 * @returns The JWT string or null if not found
 */
export function getKioskToken(): string | null {
    if (typeof window === 'undefined') return null;
    return window.localStorage.getItem('fw_kiosk_jwt');
}

/**
 * Clears the kiosk JWT and redirects to setup
 */
export function clearKioskSession(): void {
    try {
        window.localStorage.removeItem('fw_kiosk_jwt');
    } catch {
        // Ignore localStorage errors
    }
  try {
    // Best-effort native clear for Electron shell persistence.
    void window.kiosk?.clearToken?.();
  } catch {
    // Ignore native bridge errors.
  }
    window.location.replace('/kiosk/setup');
}

// ============================================================================
// Supabase Client
// ============================================================================

/** Cached kiosk Supabase client (reused for same JWT) */
let cachedClient: SupabaseClient | null = null;
let cachedJwt: string | null = null;

/**
 * Creates or returns a cached Supabase client configured with the kiosk JWT.
 * 
 * The client is cached and reused as long as the JWT doesn't change.
 * This prevents creating a new client on every network request,
 * which is critical for efficiency with 100+ concurrent screens.
 * 
 * @returns The configured Supabase client or null if configuration is missing
 */
export function getKioskSupabaseClient(): SupabaseClient | null {
    const jwt = getKioskToken();

    if (!jwt || !supabaseUrl || !supabaseAnonKey) {
        return null;
    }

    // Return cached client if JWT hasn't changed
    if (cachedClient && cachedJwt === jwt) {
        return cachedClient;
    }

    // Create new client with current JWT
    cachedClient = createClient(supabaseUrl, supabaseAnonKey, {
        global: {
            headers: {
                Authorization: `Bearer ${jwt}`
            }
        }
    });
    cachedJwt = jwt;

    return cachedClient;
}

/**
 * Validates that Supabase environment is configured
 * @returns true if configuration is valid
 */
export function isSupabaseConfigured(): boolean {
    return !!(supabaseUrl && supabaseAnonKey);
}

// ============================================================================
// Network Request Intervals
// ============================================================================

/**
 * Standard polling intervals for kiosk displays (in milliseconds).
 * 
 * These are tuned for 100+ concurrent screens:
 * - FlexBand data: 60s (student enrollments change infrequently)
 * - Substitution data: 30s (teachers need slightly faster updates)  
 * - Heartbeat: 30s (monitor online status)
 * - JWT expiration check: 60s (security)
 * - Auto-update check: 3600s (app version, hourly)
 */
export const KIOSK_INTERVALS = {
    /** FlexBand data polling (60 seconds) */
    FLEXBAND_POLL: 60_000,
    /** Substitution data polling (30 seconds) */
    SUBSTITUTION_POLL: 30_000,
    /** Heartbeat to server (30 seconds) */
    HEARTBEAT: 30_000,
    /** JWT expiration check (60 seconds) */
    JWT_CHECK: 60_000,
    /** Auto-update version check (1 hour) */
    VERSION_CHECK: 3_600_000,
} as const;
